import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import * as Sentry from "@sentry/react";

import { medications } from "@api/index";

interface MedicationSuggestion {
  displayName: string;
}

interface MedicationSearchResponse {
  suggestions: MedicationSuggestion[];
  hasError: boolean;
}

interface UseMedicationSuggestionsResult {
  suggestions: string[];
  isLoading: boolean;
  hasError: boolean;
  search: (query: string) => void;
}

const DEBOUNCE_MS = 300;

/**
 * Debounced, cancellation-safe search against Runway's own medication-search
 * proxy (never NLM directly) — see docs/specs/medication-search-input.md
 * (B2, B10, B11). A newer keystroke always aborts the previous in-flight
 * request so a stale response can never overwrite a fresher one; an aborted
 * request is never itself surfaced as a search failure.
 */
const useMedicationSuggestions = (): UseMedicationSuggestionsResult => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      abortControllerRef.current?.abort();
    };
  }, []);

  const runSearch = useCallback(async (query: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    try {
      const { data } = await medications.medicationsGet(`/search?terms=${encodeURIComponent(query)}`, {
        signal: controller.signal,
      });
      if (!isMountedRef.current) {
        return;
      }
      const response = data as MedicationSearchResponse;
      setSuggestions(response.suggestions.map((suggestion) => suggestion.displayName));
      setHasError(response.hasError);
    } catch (error) {
      if (axios.isCancel(error)) {
        // Superseded by a newer search -- not a failure, ignore silently.
        return;
      }
      // Report regardless of mount state -- this is telemetry, not a state
      // update, and must not be lost just because the component unmounted.
      Sentry.captureException(error);
      if (!isMountedRef.current) {
        return;
      }
      setSuggestions([]);
      setHasError(true);
    } finally {
      if (isMountedRef.current && abortControllerRef.current === controller) {
        setIsLoading(false);
      }
    }
  }, []);

  const search = useCallback(
    (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!query.trim()) {
        abortControllerRef.current?.abort();
        setSuggestions([]);
        setHasError(false);
        setIsLoading(false);
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        runSearch(query);
      }, DEBOUNCE_MS);
    },
    [runSearch]
  );

  return { suggestions, isLoading, hasError, search };
};

export default useMedicationSuggestions;
